import { useEffect, useState } from "react";
import { api } from "./lib/api";

// ── HOW LONG CALLS RUN (0380) ───────────────────────────────────────────────────────────────────
//
// Caleb, 2026-10-25: "how often does that actually get hit ... if ... 9900 of them ... average out to
// like a minute ... and then we've got 100 calls that go to 30 minutes ... that would indicate that the
// ... 30 minutes is too long ... we can probably drop that down to more like 10 or 15 minutes". Paid
// calls of the last 30 days, every campaign, by length; how many ran to the limit and what those cost
// both sides; and the limit itself, which every app reads before each call.

type Lengths = { days: number; max_minutes: number; warn_before_minutes: number; calls: number; answered: number;
  buckets: { label: string; n: number }[]; hit_limit: number; avg_answered_seconds: number | null;
  longest_under_limit_seconds: number | null; limit_paid_cents: number; limit_cost_cents: number };

const dur = (s: number | null) => s == null ? "—" : s >= 60 ? `${Math.floor(s / 60)} min ${Math.round(s % 60)} s` : `${Math.round(s)} s`;
const money = (c: number) => `$${(c / 100).toFixed(2)}`;

export function CallLengths() {
  const [d, setD] = useState<Lengths | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [max, setMax] = useState("30");
  const [warn, setWarn] = useState("5");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const load = () => api<Lengths>("/staff/call-lengths").then((x) => { setD(x); setErr(null); setMax(String(x.max_minutes)); setWarn(String(x.warn_before_minutes)); })
    .catch((e) => setErr(String(e?.message ?? e)));
  useEffect(() => { load(); }, []);
  if (err) return <div className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm">Could not load call lengths: {err}</div>;
  if (!d) return null;
  const rows = [...d.buckets, { label: "Hit the limit", n: d.hit_limit }];
  const top = Math.max(1, ...rows.map((r) => r.n));
  async function save() {
    setBusy(true); setSaid(null);
    try {
      await api("/staff/call-limit", { method: "POST", body: JSON.stringify({ max_call_minutes: parseInt(max, 10), warn_before_minutes: parseInt(warn, 10) }) });
      setSaid("Saved. Calls from now on use it."); load();
    } catch (e: any) { setSaid(String(e?.message ?? e)); } finally { setBusy(false); }
  }
  return (
    <div className="rounded-xl border border-border px-4 py-3 text-sm">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span className="font-bold">Calls · last {d.days} days, every campaign</span>
        <span className="text-muted">{d.calls.toLocaleString()} calls · {d.answered.toLocaleString()} answered</span>
      </div>
      <table className="w-full"><tbody>
        {rows.map((r, i) => {
          const last = i === rows.length - 1;
          return (
            <tr key={r.label}>
              <td className={"py-0.5 pr-3 whitespace-nowrap w-28 " + (last ? "font-bold text-amber-700" : "")}>{r.label}</td>
              <td className="py-0.5"><div className={"h-3 rounded-r " + (last ? "bg-amber-500" : "bg-blue-500")}
                style={{ width: `${r.n ? Math.max(1, (r.n / top) * 100) : 0}%` }} /></td>
              <td className={"py-0.5 pl-3 text-right tabular-nums w-20 " + (last ? "font-bold text-amber-700" : "")}>{r.n.toLocaleString()}</td>
            </tr>
          );
        })}
      </tbody></table>
      <div className="mt-2">Average answered call <b>{dur(d.avg_answered_seconds)}</b> · longest that ended on its own <b>{dur(d.longest_under_limit_seconds)}</b></div>
      <div>Calls that hit the limit: campaigns paid <b>{money(d.limit_paid_cents)}</b>, cost us <b>{money(d.limit_cost_cents)}</b></div>
      <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-border">
        A call ends at <input className="ctl" style={{ width: 64 }} value={max} inputMode="numeric" onChange={(e) => setMax(e.target.value)} /> minutes,
        warned <input className="ctl" style={{ width: 56 }} value={warn} inputMode="numeric" onChange={(e) => setWarn(e.target.value)} /> minutes before
        <span className="flex-1" />
        <button className="btn disabled:opacity-50" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
      </div>
      {said && <div className="text-xs mt-1">{said}</div>}
    </div>
  );
}
